import FeatureKeys from "global/constants/FeatureKeys";
import {
  allOf,
  anyOf,
  canBuildRequests,
  canUseAiTools,
  feature,
} from "../predicates";
import type { TourStepConfig } from "../../types/tour.types";

const RAIL_SPOTLIGHT_PADDING = 12;

/** Copy shared between the standard builder and the self-sign flow. */
const SHARED = {
  fromCloud: {
    title: "Bring a file from cloud storage",
    content:
      "Pull a document straight from Google Drive, Dropbox or OneDrive without downloading it to this device first.",
  },
  upload: {
    title: "Or upload from this device",
    content:
      "Drop a file here, or click to browse. Add more than one and they are signed together as a single request.",
  },
  fieldsPanel: {
    title: "Field types",
    content:
      "Signature, initials, date, text and more. Drag one from this list onto the document to place it.",
  },
} as const;

export const builderUploadSteps: TourStepConfig[] = [
  {
    id: "builder-stepper",
    target: "request.stepper",
    title: "The four stages",
    content:
      "Upload, add recipients, place the fields, then send. Each stage opens once the one before it is complete.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-choose-template",
    target: "request.chooseTemplate",
    title: "Start from a template",
    content:
      "Pick a saved template and its document, recipients and fields come with it, so the rest of this flow is mostly already filled in.",
    placement: "bottom",
    when: allOf(canBuildRequests, feature(FeatureKeys.TEMPLATE_USE)),
  },
  {
    id: "builder-from-cloud",
    target: "request.fromCloud",
    ...SHARED.fromCloud,
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-upload",
    target: "request.upload",
    ...SHARED.upload,
    placement: "bottom",
    when: canBuildRequests,
  },
];

export const builderRecipientsSteps: TourStepConfig[] = [
  {
    id: "builder-recipient-details",
    target: "request.recipientRow",
    title: "Who signs, and where it's sent",
    content:
      "Start typing a name and anyone in your contacts is suggested — pick one and the email fills itself in. The email is where the request is delivered and where the signed copy comes back from, so every recipient needs their own.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-recipients-add",
    target: "request.recipientsAdd",
    title: "Add another recipient",
    content:
      "Add as many as the document needs. Each one gets their own row here, and their own fields to fill in on the document.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-signer-order",
    target: "request.signerOrder",
    title: "Signing order",
    content:
      "Off, everyone is asked at once. On, signing runs level by level — people in the same level are notified together, and each level waits for the one before it.",
    placement: "bottom",
    when: allOf(canBuildRequests, feature(FeatureKeys.ENVELOPE_SIGNING_ORDER)),
  },
  {
    id: "builder-approvers",
    target: "request.recipientTabs",
    title: "Approvers",
    content:
      "An approver reviews and signs off on the document first. Signers only receive it once they have — useful when something needs internal sign-off before it leaves.",
    placement: "bottom",
    when: allOf(
      canBuildRequests,
      feature(FeatureKeys.TEMPLATE_APPROVAL_WORKFLOWS),
    ),
  },
];

export const builderFieldsSteps: TourStepConfig[] = [
  {
    id: "builder-participant",
    target: "request.participantRail",
    title: "Whose fields you're placing",
    content:
      "Every field belongs to one recipient. Pick them here — each has a colour, and it follows their fields through the document.",
    placement: "right",
    when: canBuildRequests,
  },
  {
    id: "builder-fields-panel",
    target: "request.fieldsPanel",
    ...SHARED.fieldsPanel,
    placement: "right",
    allowTargetInteraction: true,
    when: canBuildRequests,
  },
  {
    id: "builder-document",
    target: "request.document",
    title: "The document",
    content:
      "Place fields anywhere, on any page — each in its recipient's colour. Drag one to move it, grab an edge to resize it, and right-click for more actions.",
    placement: "left",
    skipScroll: true,
    allowTargetInteraction: true,
    when: canBuildRequests,
  },
  {
    id: "builder-right-click",
    target: "request.pageContextMenu",
    reveal: {
      trigger: "request.documentPage",
      awaits: "request.pageContextMenu",
      via: "contextmenu",
    },
    aboveModalLayer: true,
    title: "Right-click actions",
    content:
      "This is the menu you get on the page itself: undo, redo and paste, each with its keyboard shortcut. Right-click a field you've placed instead and you get cut, copy, duplicate, delete and reassign.",
    placement: "auto",
    skipScroll: true,
    when: canBuildRequests,
  },
  {
    id: "builder-tab-documents",
    target: "request.tabDocuments",
    title: "Documents panel",
    content:
      "Opens the list of files you're sending. Switch between them, or add another without leaving the editor.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canBuildRequests,
  },
  {
    id: "builder-tab-properties",
    target: "request.tabProperties",
    title: "Field properties",
    content:
      "Select a field you've placed, then open this to name it, mark it required, change its format, or reassign it to someone else.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canBuildRequests,
  },
  {
    id: "builder-tab-assistant",
    target: "request.tabAssistant",
    title: "AI Assistant",
    content:
      "Reads the document you're about to send and answers questions about it — who signs first, or what a particular clause commits you to.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canUseAiTools,
  },
  {
    id: "builder-tab-insights",
    target: "request.tabInsights",
    title: "AI Insights",
    content:
      "Pulls out the dates, obligations and unusual clauses worth a second look before the document goes out.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canUseAiTools,
  },
];

export const builderSendSteps: TourStepConfig[] = [
  {
    id: "builder-email-content",
    target: "request.emailContent",
    reveal: {
      trigger: "request.emailContentHead",
      awaits: "request.emailContent",
    },
    title: "Email subject and message",
    content:
      "The subject is required — for most recipients it is all they see before deciding to open it. The message below is optional: say what the document is and why it needs signing.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-reminders",
    target: "request.reminders",
    reveal: {
      trigger: "request.remindersHead",
      awaits: "request.reminders",
    },
    title: "Reminders and expiration",
    content:
      "Reminders go only to the people who still haven't signed, on a schedule you set. Expiry closes the request after a set number of days — the exact date appears as you type.",
    placement: "bottom",
    when: allOf(
      canBuildRequests,
      anyOf(
        feature(FeatureKeys.REMINDER_AUTOMATIC),
        feature(FeatureKeys.ENVELOPE_AUTO_EXPIRATION),
      ),
    ),
  },
  {
    id: "builder-permissions",
    target: "request.permissions",
    reveal: {
      trigger: "request.permissionsHead",
      awaits: "request.permissions",
    },
    title: "Signer permissions",
    content:
      "Four switches covering downloads, completion emails, attached copies and delegation. Tighten them for a confidential agreement; leave them on when speed matters more.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "builder-schedule",
    target: "request.schedule",
    reveal: {
      trigger: "request.scheduleHead",
      awaits: "request.schedule",
    },
    title: "When it sends",
    content:
      "Send immediately, or hold the request for a date and time you pick. Reminders and expiry are counted from when it actually sends, not from now.",
    placement: "bottom",
    when: allOf(canBuildRequests, feature(FeatureKeys.ENVELOPE_SCHEDULED_SEND)),
  },
];

export const builderBulkSignersSteps: TourStepConfig[] = [
  {
    id: "bulk-import-csv",
    target: "bulk.importCsv",
    title: "Bring the whole list in at once",
    content:
      "Upload a CSV with a name and an email per row. You map your columns to those two fields as you import, so an export from somewhere else works without reshaping it first.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "bulk-add-signer",
    target: "bulk.addSigner",
    title: "Or add people one at a time",
    content:
      "Adds a blank row you can type straight into — useful for the handful who arrive after the spreadsheet is done.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "bulk-signers-bar",
    target: "bulk.signersBar",
    title: "Every row is checked before you send",
    content:
      "Rows are validated as they arrive and counted here. Filter to the ones with problems to fix them in place, or clear them all in one go — then everyone left gets their own separate copy to sign.",
    placement: "bottom",
    when: canBuildRequests,
  },
];

export const selfSignUploadSteps: TourStepConfig[] = [
  {
    id: "self-sign-stepper",
    target: "request.stepper",
    title: "Two stages, no recipients",
    content:
      "Add your document, then place what you need to fill and sign it. Nothing is emailed to anyone — the finished copy is yours.",
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "self-sign-from-cloud",
    target: "request.fromCloud",
    ...SHARED.fromCloud,
    placement: "bottom",
    when: canBuildRequests,
  },
  {
    id: "self-sign-upload",
    target: "request.upload",
    ...SHARED.upload,
    placement: "bottom",
    when: canBuildRequests,
  },
];

export const selfSignEditorSteps: TourStepConfig[] = [
  {
    id: "self-sign-signer",
    target: "request.participantRail",
    title: "You're the only signer",
    content:
      "Every field you place belongs to you, so there is nobody to assign anything to — just put what you need on the page.",
    placement: "right",
    when: canBuildRequests,
  },
  {
    id: "self-sign-fields-panel",
    target: "request.fieldsPanel",
    ...SHARED.fieldsPanel,
    placement: "right",
    allowTargetInteraction: true,
    when: canBuildRequests,
  },
  {
    id: "self-sign-document",
    target: "request.document",
    title: "The document",
    content:
      "Place fields anywhere, on any page. Drag one to move it, grab an edge to resize it, and right-click for more actions.",
    placement: "left",
    skipScroll: true,
    allowTargetInteraction: true,
    when: canBuildRequests,
  },
  {
    id: "self-sign-right-click",
    target: "request.pageContextMenu",
    reveal: {
      trigger: "request.documentPage",
      awaits: "request.pageContextMenu",
      via: "contextmenu",
    },
    aboveModalLayer: true,
    title: "Right-click actions",
    content:
      "This is the menu you get on the page itself: undo, redo and paste, each with its keyboard shortcut. Right-click a field you've placed instead and you get cut, copy, duplicate and delete.",
    placement: "auto",
    skipScroll: true,
    when: canBuildRequests,
  },
  {
    id: "self-sign-tab-documents",
    target: "request.tabDocuments",
    title: "Documents panel",
    content:
      "Opens the list of files you're signing. Switch between them, or add another without leaving the editor.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canBuildRequests,
  },
  {
    id: "self-sign-tab-properties",
    target: "request.tabProperties",
    title: "Field properties",
    content:
      "Select a field you've placed, then open this to name it, mark it required, or change its format.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canBuildRequests,
  },
  {
    id: "self-sign-tab-assistant",
    target: "request.tabAssistant",
    title: "AI Assistant",
    content:
      "Reads the document you're about to sign and answers questions about it — what a particular clause commits you to.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canUseAiTools,
  },
  {
    id: "self-sign-tab-insights",
    target: "request.tabInsights",
    title: "AI Insights",
    content:
      "Pulls out the dates, obligations and unusual clauses worth a second look before you sign.",
    placement: "left",
    spotlightPadding: RAIL_SPOTLIGHT_PADDING,
    when: canUseAiTools,
  },
];
